import React, { useEffect, useState } from "react";
import { api, fmtSDG } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CreditCard, Printer, Coffee, QrCode, Copy, FileText } from "lucide-react";
import { toast } from "sonner";
import { printReceipt } from "@/lib/printer";
import { useNavigate } from "react-router-dom";

const STATUS_STYLES = {
    free: "table-free",
    occupied: "table-occupied",
    billing: "table-billing",
};

export default function TableManagement() {
    const { t, lang, user } = useApp();
    const navigate = useNavigate();
    const [tables, setTables] = useState([]);
    const [selected, setSelected] = useState(null);    // table object
    const [activeOrder, setActiveOrder] = useState(null); // order on that table
    const [loading, setLoading] = useState(false);
    const [showQr, setShowQr] = useState(false);

    const load = async () => {
        const r = await api.get("/tables");
        setTables(r.data);
    };

    useEffect(() => {
        load();
        const i = setInterval(load, 5000);
        return () => clearInterval(i);
    }, []);

    const openTable = async (tb) => {
        setSelected(tb);
        setActiveOrder(null);
        setShowQr(false);
        setLoading(true);
        try {
            // try current_order_id first; fall back to a search
            if (tb.current_order_id) {
                try {
                    const r = await api.get(`/orders/${tb.current_order_id}`);
                    if (["pending", "confirmed", "delivered", "on_hold"].includes(r.data.status)) {
                        setActiveOrder(r.data);
                    }
                } catch (e) { /* not found */ }
            }
            if (!tb.current_order_id || activeOrder === null) {
                const r = await api.get("/orders");
                const found = r.data.find(
                    (o) => o.table_number === tb.number
                        && ["pending", "confirmed", "delivered", "on_hold"].includes(o.status)
                );
                if (found) setActiveOrder(found);
            }
        } finally {
            setLoading(false);
        }
    };

    const closeDialog = () => {
        setSelected(null);
        setActiveOrder(null);
        setShowQr(false);
    };

    const payAndClose = async () => {
        if (!activeOrder) return;
        try {
            // ensure confirmed first
            if (activeOrder.status === "pending") {
                await api.post(`/orders/${activeOrder.id}/confirm`);
            }
            await api.post(`/orders/${activeOrder.id}/close`);
            toast.success(t("order_closed"));
            closeDialog();
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    const printNow = async () => {
        if (!activeOrder) return;
        const settings = (await api.get("/settings")).data;
        await printReceipt({ order: activeOrder, settings, lang });
    };

    const menuLink = (n) => {
        const saved = localStorage.getItem("cafe24_qr_base_url");
        const base = saved || (window.location.origin.includes("preview.emergentagent") ? "https://cafe24.space" : window.location.origin);
        return `${base.replace(/\/+$/, "")}/menu?table=${n}`;
    };
    const copyLink = (n) => { navigator.clipboard.writeText(menuLink(n)); toast.success(t("copied")); };

    const summary = tables.reduce((acc, x) => { acc[x.status] = (acc[x.status] || 0) + 1; return acc; }, {});

    return (
        <div className="space-y-4" data-testid="tables-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className={`${lang === "ar" ? "font-ar" : "font-display-en italic"} text-3xl`}>{t("manage_tables")}</h1>
                <div className="flex items-center gap-3 flex-wrap">
                    {user?.role === "owner" && (
                        <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/qr-print")} data-testid="qr-sheet-btn">
                            <FileText className="h-4 w-4 me-2" />
                            {lang === "ar" ? "طباعة QR للطاولات" : "Print Table QRs"}
                        </Button>
                    )}
                    <div className="flex gap-2 text-sm">
                        <span className="px-3 py-1 rounded-full table-free">{t("free")} {summary.free || 0}</span>
                        <span className="px-3 py-1 rounded-full table-occupied">{t("occupied")} {summary.occupied || 0}</span>
                        <span className="px-3 py-1 rounded-full table-billing">{t("billing")} {summary.billing || 0}</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
                {tables.map((tb) => (
                    <button
                        key={tb.id}
                        onClick={() => openTable(tb)}
                        data-testid={`table-card-${tb.number}`}
                        className={`aspect-square rounded-xl border ${STATUS_STYLES[tb.status]} flex flex-col items-center justify-center p-2 grain active:scale-95 transition-all`}
                    >
                        <div className="text-2xl font-medium tabular-nums">{tb.number}</div>
                        <div className="text-[10px] uppercase tracking-wider mt-1 opacity-80">{t(tb.status)}</div>
                    </button>
                ))}
            </div>

            <Dialog open={!!selected} onOpenChange={(o) => !o && closeDialog()}>
                <DialogContent data-testid="table-dialog" className="max-h-[90vh] overflow-y-auto">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center justify-between">
                                    <span>{t("table")} #{selected.number}</span>
                                    <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_STYLES[selected.status]}`}>
                                        {t(selected.status)}
                                    </span>
                                </DialogTitle>
                            </DialogHeader>

                            {loading && <div className="text-sm text-muted-foreground py-6 text-center">{t("loading")}</div>}

                            {!loading && activeOrder && (
                                <div className="space-y-4">
                                    <div className="text-xs text-muted-foreground">
                                        {t("order_no")} #{activeOrder.order_no} · {activeOrder.waiter_name}
                                        {activeOrder.cashier_name ? ` → ${activeOrder.cashier_name}` : ""}
                                    </div>
                                    <div className="rounded-xl border border-border divide-y divide-border max-h-80 overflow-y-auto">
                                        {activeOrder.items.map((it, i) => (
                                            <div key={i} className="p-3 flex items-center gap-3" data-testid={`pay-item-${i}`}>
                                                <span className="text-muted-foreground tabular-nums font-medium min-w-[2rem]">{it.qty}×</span>
                                                <span className="flex-1">{lang === "ar" ? it.name_ar : it.name_en}</span>
                                                <span className="tabular-nums">{fmtSDG(it.price * it.qty, lang)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="space-y-1.5">
                                        {activeOrder.tax > 0 && (
                                            <div className="flex justify-between text-sm text-muted-foreground">
                                                <span>{t("subtotal")}</span>
                                                <span className="tabular-nums">{fmtSDG(activeOrder.subtotal, lang)}</span>
                                            </div>
                                        )}
                                        {activeOrder.tax > 0 && (
                                            <div className="flex justify-between text-sm text-muted-foreground">
                                                <span>{t("tax")}</span>
                                                <span className="tabular-nums">{fmtSDG(activeOrder.tax, lang)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between pt-2 border-t border-border">
                                            <span className="text-muted-foreground">{t("total")}</span>
                                            <span className="text-primary text-2xl font-medium tabular-nums">{fmtSDG(activeOrder.total, lang)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!loading && !activeOrder && (
                                <div className="py-10 text-center">
                                    <Coffee className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                                    <div className="text-sm text-muted-foreground">{t("no_active_order")}</div>
                                </div>
                            )}

                            {/* Owner-only collapsible QR (still useful for printing stickers) */}
                            {user?.role === "owner" && (
                                <div className="border-t border-border pt-3">
                                    <button
                                        onClick={() => setShowQr((v) => !v)}
                                        className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-2"
                                        data-testid="toggle-qr"
                                    >
                                        <QrCode className="h-3.5 w-3.5" /> {showQr ? "—" : "+"} QR
                                    </button>
                                    {showQr && (
                                        <div className="mt-3 space-y-2">
                                            <img
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(menuLink(selected.number))}&color=1A0F08&bgcolor=FDFAF5`}
                                                alt="QR"
                                                className="rounded-lg border border-border p-2 bg-[#fdfaf5] mx-auto"
                                                data-testid="table-qr-image"
                                            />
                                            <Button variant="outline" size="sm" className="w-full" onClick={() => copyLink(selected.number)} data-testid="copy-link-btn">
                                                <Copy className="h-3.5 w-3.5 me-2" />{t("copy_link")}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            <DialogFooter className="gap-2">
                                {activeOrder && (
                                    <>
                                        <Button variant="outline" onClick={printNow} data-testid="table-print-btn">
                                            <Printer className="h-4 w-4 me-2" />{t("print_receipt")}
                                        </Button>
                                        <Button onClick={payAndClose} className="flex-1 h-14 text-base" data-testid="table-pay-btn">
                                            <CreditCard className="h-5 w-5 me-2" />
                                            {t("pay_now")} · {fmtSDG(activeOrder.total, lang)}
                                        </Button>
                                    </>
                                )}
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
